import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { useOrgConnectedAppsRates } from '../../banners/hooks/useOrgConnectedAppsRates.ts';
import { getSmartLinkIcon } from '../../utils/smartLinkIcon.ts';
import type { EnhancedSmartLink } from '../../utils/smartLinkTypes.ts';
import {
	isUnconnectedThirdParty,
	pickTopUnconnectedApp,
	toTraitKey,
} from '../../utils/unconnectedThirdPartyApps.ts';
import {
	HIGH_ADOPTION_THRESHOLD,
	OrgConnectedAppsPromoBanner,
} from '../OrgConnectedAppsPromoBanner.tsx';
import {
	getBannerDismissedEvent,
	getBannerShownEvent,
	type OrgBannerVariant,
	type OrgIneligibleReason,
	type PromoBannerAttributes,
} from '../promoBannerAnalytics.ts';

import { useBannerDismissal } from './useBannerDismissal.ts';

export type UseOrgConnectedAppsPromoBannerInput = {
	links: EnhancedSmartLink[];
	contentId: string | undefined;
};

export type UseOrgConnectedAppsPromoBannerResult = {
	element: React.ReactElement | null;
	/** Ineligibility reason; `undefined` while loading or when shown. Read by the footer. */
	ineligibleReason: OrgIneligibleReason | undefined;
};

export const useOrgConnectedAppsPromoBanner = ({
	links,
	contentId,
}: UseOrgConnectedAppsPromoBannerInput): UseOrgConnectedAppsPromoBannerResult => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const unconnectedThirdPartyLinks = links.filter(isUnconnectedThirdParty);
	const hasUnconnectedThirdParty = unconnectedThirdPartyLinks.length > 0;

	const { value: orgRates, loading: isRatesLoading } = useOrgConnectedAppsRates({
		skip: !hasUnconnectedThirdParty,
	});

	const unconnectedTraitKeys = new Set<string>();
	for (const l of unconnectedThirdPartyLinks) {
		const traitKey = toTraitKey(l.key);
		if (traitKey) {
			unconnectedTraitKeys.add(traitKey);
		}
	}
	const topApp = pickTopUnconnectedApp(orgRates, unconnectedTraitKeys);

	const { isDismissed, dismiss } = useBannerDismissal(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_BYLINE_CONNECTIONS_ORG_CONNECTED_APPS_BANNER_DISMISSED_PAGES,
		contentId,
	);

	// First terminal failure in check order; loading yields `undefined` (not reported).
	let ineligibleReason: OrgIneligibleReason | undefined;
	if (!hasUnconnectedThirdParty) {
		ineligibleReason = 'noUnconnectedThirdParty';
	} else if (isRatesLoading) {
		ineligibleReason = undefined;
	} else if (!topApp) {
		ineligibleReason = 'noTopApp';
	} else if (isDismissed) {
		ineligibleReason = 'dismissed';
	}

	const shouldShow = !isRatesLoading && ineligibleReason === undefined && !!topApp;

	const shownAttributes: PromoBannerAttributes | undefined = useMemo(
		() =>
			topApp
				? {
						bannerType: 'orgConnectedApps',
						connector: topApp.traitKey,
						variant: (topApp.percent >= HIGH_ADOPTION_THRESHOLD
							? 'highAdoption'
							: 'lowAdoption') satisfies OrgBannerVariant,
						topAppPercent: topApp.percent,
					}
				: undefined,
		[topApp],
	);

	const hasFiredShownRef = useRef(false);
	useEffect(() => {
		if (!shouldShow || !shownAttributes || hasFiredShownRef.current) {
			return;
		}
		hasFiredShownRef.current = true;
		createAnalyticsEvent(getBannerShownEvent(shownAttributes)).fire();
	}, [shouldShow, shownAttributes, createAnalyticsEvent]);

	const handleDismiss = useCallback(() => {
		if (shownAttributes) {
			createAnalyticsEvent(getBannerDismissedEvent(shownAttributes)).fire();
		}
		dismiss();
	}, [createAnalyticsEvent, shownAttributes, dismiss]);

	if (!shouldShow || !topApp) {
		return { element: null, ineligibleReason };
	}

	const matchingLink = unconnectedThirdPartyLinks.find(
		(l) => toTraitKey(l.key) === topApp.traitKey,
	);
	const productLabel = matchingLink?.product ?? matchingLink?.authProviders[0]?.displayName ?? '';
	const icon = matchingLink ? getSmartLinkIcon(matchingLink) : undefined;

	return {
		element: (
			<OrgConnectedAppsPromoBanner
				icon={icon}
				productLabel={productLabel}
				topAppPercent={topApp.percent}
				onDismiss={handleDismiss}
			/>
		),
		ineligibleReason: undefined,
	};
};
